import { Layout } from 'antd';
import { useCallback, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';

import AppHeader from './AppHeader';
import AppSider from './AppSider';
import AppVersion from './AppVersion';

import { useAuthStore } from '@/core/auth/authStore';
import { isAdminRole, useAuthUser, useIsPlatformAdmin } from '@/core/auth/useAuth';


const { Content, Footer } = Layout;

const SIDER_COLLAPSED_KEY = 'pymestock-sider-collapsed';

function getPersistedCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDER_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Smart shell layout — wires auth state to the dumb AppSider and AppHeader.
 * Persists sidebar collapsed state to localStorage.
 */
export default function AppLayout() {
  const [collapsed, setCollapsed] = useState<boolean>(getPersistedCollapsed);
  const user = useAuthUser();
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const handleCollapse = useCallback((value: boolean) => {
    setCollapsed(value);
    try {
      localStorage.setItem(SIDER_COLLAPSED_KEY, String(value));
    } catch {
      // ignore storage errors
    }
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    void navigate('/login', { replace: true });
  }, [logout, navigate]);

  const isAdmin = isAdminRole(user?.role);
  const isPlatformAdmin = useIsPlatformAdmin();

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppSider
        collapsed={collapsed}
        onCollapse={handleCollapse}
        isAdmin={isAdmin}
        isPlatformAdmin={isPlatformAdmin}
      />

      <Layout>
        <AppHeader
          email={user?.email ?? ''}
          name={user?.name ?? ''}
          role={user?.role ?? ''}
          onLogout={handleLogout}
        />
        <Content
          style={{
            margin: '24px',
            minHeight: 'calc(100vh - 64px - 48px - 40px)',
          }}
        >
          <Outlet />
        </Content>
        <Footer
          style={{
            height: 40,
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
          }}
        >
          <AppVersion />
        </Footer>
      </Layout>
    </Layout>
  );
}
